import { UseCases } from "@planzone/core";
import { Roles } from "./types";

const anyone = [
  Roles.Admin,
  Roles.SubAdmin,
  Roles.User,
  Roles.Visitor,
  Roles.ProjectManager,
  Roles.Worker,
  Roles.Observer,
];

const projectManagers = [Roles.Admin, Roles.ProjectManager];

const projectMembers = [Roles.Admin, Roles.ProjectManager, Roles.Worker];

const permissions: { [Key in UseCases]: Roles[] } = {
  [UseCases.authenticate]: anyone,
  [UseCases.logOut]: anyone,
  [UseCases.createAccount]: anyone,
  [UseCases.confirmAccountCreation]: anyone,
  [UseCases.editUserPreferences]: anyone,
  [UseCases.retrieveUserPreferences]: anyone,
  [UseCases.retrievePersonalWeeklyCalendar]: anyone,
  [UseCases.editPersonalWeeklyCalendar]: anyone,
  [UseCases.changePassword]: anyone,
  [UseCases.inviteMembers]: [Roles.Admin],
  [UseCases.fillAccount]: anyone,
  [UseCases.allocateResources]: projectManagers,
  [UseCases.editAllocation]: projectManagers,
  [UseCases.checkAllocationWarnings]: projectManagers,
  [UseCases.removeAllocation]: projectManagers,
  [UseCases.sendActivityReport]: anyone,
  [UseCases.retrievePreferences]: anyone,
  [UseCases.editPreferences]: anyone,
  [UseCases.retrieveRoles]: anyone,
  [UseCases.retrievePlanzoneRole]: anyone,
  [UseCases.retrieveZoneRole]: anyone,
  [UseCases.retrieveProjectRole]: anyone,
  [UseCases.retrieveProjectsRoles]: anyone,
  [UseCases.retrieveZonesIndicators]: anyone,
  [UseCases.retrievePlanzones]: anyone,
  [UseCases.changePlanzone]: anyone,
  [UseCases.retrieveCurrentPlanzone]: anyone,
  [UseCases.retrievePersonalSynthesis]: anyone,
  [UseCases.updatePlanzoneContacts]: [Roles.Admin],
  [UseCases.retrieveOrganization]: [Roles.Admin],
  [UseCases.editOrganization]: [Roles.Admin],
  [UseCases.retrievePlanzoneSettings]: [Roles.Admin],
  [UseCases.editPlanzoneSettings]: [Roles.Admin],
  [UseCases.addProjectExternalLink]: projectManagers,
  [UseCases.editProjectExternalLink]: projectManagers,
  [UseCases.removeProjectExternalLink]: projectManagers,
  [UseCases.retrieveProjectExternalLinks]: anyone,
  [UseCases.createList]: projectManagers,
  [UseCases.duplicateList]: projectManagers,
  [UseCases.createEvent]: projectManagers,
  [UseCases.editEvent]: projectManagers,
  [UseCases.retrieveEvent]: anyone,
  [UseCases.removeEvent]: projectManagers,
  [UseCases.createMilestone]: projectManagers,
  [UseCases.createTask]: projectManagers,
  [UseCases.createKanbanTask]: projectManagers,
  [UseCases.createTaskStatus]: projectManagers,
  [UseCases.editList]: projectManagers,
  [UseCases.editMilestone]: projectManagers,
  [UseCases.editTask]: projectManagers,
  [UseCases.editTaskStatus]: projectManagers,
  [UseCases.markMilestoneAsCompleted]: projectMembers,
  [UseCases.markTaskAsCompleted]: projectMembers,
  [UseCases.moveKanbanTask]: projectManagers,
  [UseCases.moveListElement]: projectManagers,
  [UseCases.removeList]: projectManagers,
  [UseCases.removeTask]: projectManagers,
  [UseCases.removeTaskStatus]: projectManagers,
  [UseCases.retrieveKanban]: anyone,
  [UseCases.retrieveList]: anyone,
  [UseCases.retrieveLists]: anyone,
  [UseCases.retrieveMilestone]: anyone,
  [UseCases.retrieveProjectGantt]: anyone,
  [UseCases.retrieveTask]: anyone,
  [UseCases.retrieveTasks]: anyone,
  [UseCases.retrieveTaskStatuses]: anyone,
  [UseCases.removeMilestone]: projectManagers,
  [UseCases.editProjectReportTemplate]: [Roles.Admin],
  [UseCases.retrieveProjectReportTemplate]: anyone,
  [UseCases.submitProjectReport]: projectManagers,
  [UseCases.retrieveProjectReports]: anyone,
  [UseCases.retrieveProjectReport]: anyone,
  [UseCases.removeProjectReport]: projectManagers,
  [UseCases.editProjectReport]: projectManagers,
  [UseCases.retrieveProject]: anyone,
  [UseCases.retrieveProjects]: anyone,
  [UseCases.createProject]: [Roles.Admin, Roles.SubAdmin],
  [UseCases.editProject]: projectManagers,
  [UseCases.removeProject]: [Roles.Admin, Roles.ProjectManager],
  [UseCases.editProjectWeatherReport]: projectManagers,
  [UseCases.retrieveProjectSynthesis]: anyone,
  [UseCases.retrieveProjectHistory]: anyone,
  [UseCases.pushProjectHistory]: anyone,
  [UseCases.configureProjectTools]: projectManagers,
  [UseCases.retrieveProjectToolsConfiguration]: projectManagers,
  [UseCases.retrieveProjectModels]: [Roles.Admin, Roles.SubAdmin],
  [UseCases.useProjectModel]: [Roles.Admin, Roles.SubAdmin],
  [UseCases.createProjectModelFromProject]: [Roles.Admin],
  [UseCases.createProjectStatus]: [Roles.Admin],
  [UseCases.retrieveProjectStatuses]: anyone,
  [UseCases.removeProjectStatus]: [Roles.Admin],
  [UseCases.editProjectStatus]: [Roles.Admin],
  [UseCases.retrieveResource]: [Roles.Admin],
  [UseCases.retrieveProjectResources]: anyone,
  [UseCases.addPlanzoneMaterialResource]: [Roles.Admin],
  [UseCases.addPlanzoneVirtualResource]: [Roles.Admin],
  [UseCases.retrievePlanzoneResources]: anyone,
  [UseCases.retrieveZoneResources]: anyone,
  [UseCases.retrievePlanzoneResourcesListing]: anyone,
  [UseCases.retrieveProjectResourcesListing]: anyone,
  [UseCases.retrieveFilterResources]: anyone,
  [UseCases.retrieveResourcesByUuids]: anyone,
  [UseCases.retrievePlanzoneResourcesCounts]: anyone,
  [UseCases.addProjectMaterialResource]: projectManagers,
  [UseCases.addProjectVirtualResource]: projectManagers,
  [UseCases.addPlanzoneResourceToProject]: projectManagers,
  [UseCases.addPlanzoneResourcesToProject]: projectManagers,
  [UseCases.addEveryoneToProject]: projectManagers,
  [UseCases.removeResourceFromPlanzone]: [Roles.Admin],
  [UseCases.editResource]: [Roles.Admin],
  [UseCases.removeResourceFromProject]: projectManagers,
  [UseCases.editProjectResource]: projectManagers,
  [UseCases.editResourceWeeklyCalendar]: [Roles.Admin],
  [UseCases.addResourceGroup]: projectManagers,
  [UseCases.retrieveResourceGroups]: anyone,
  [UseCases.removeResourceGroup]: projectManagers,
  [UseCases.editResourceGroup]: projectManagers,
  [UseCases.addResourceToResourceGroup]: projectManagers,
  [UseCases.addEveryoneToResourceGroup]: projectManagers,
  [UseCases.removeResourceFromResourceGroup]: projectManagers,
  [UseCases.retrieveResourceGroup]: anyone,
  [UseCases.assignTag]: projectMembers,
  [UseCases.removeTag]: projectMembers,
  [UseCases.retrieveProjectsElementsTags]: anyone,
  [UseCases.retrieveProjectsTags]: anyone,
  [UseCases.retrieveResourcesTags]: anyone,
  [UseCases.retrieveProjectResourcesTags]: anyone,
  [UseCases.retrieveZonesProjectsGantt]: anyone,
  [UseCases.retrieveZonesProjectsReports]: anyone,
  [UseCases.retrieveZones]: anyone,
  [UseCases.retrieveZone]: anyone,
  [UseCases.removeZone]: [Roles.Admin],
  [UseCases.transferProjects]: [Roles.Admin, Roles.SubAdmin],
  [UseCases.createZone]: [Roles.Admin],
  [UseCases.editZone]: [Roles.Admin],
  [UseCases.retrieveZoneProjects]: anyone,
  [UseCases.retrieveZonesProjects]: anyone,
  [UseCases.retrieveZoneProjectsReports]: anyone,
  [UseCases.retrieveCalendar]: anyone,
  [UseCases.retrieveDiscussion]: anyone,
  [UseCases.postComment]: anyone,
  [UseCases.startDiscussion]: projectMembers,
  [UseCases.removeComment]: projectManagers,
  [UseCases.removeDiscussion]: projectManagers,
  [UseCases.retrieveDiscussions]: anyone,
  [UseCases.retrieveProjectPortfolios]: anyone,
  [UseCases.createProjectPortfolio]: [Roles.Admin],
  [UseCases.assignProjectToPortfolio]: [Roles.Admin],
  [UseCases.removeProjectPortfolio]: [Roles.Admin],
  [UseCases.removeProjectFromPortfolio]: [Roles.Admin],
  [UseCases.editProjectPortfolio]: [Roles.Admin],
  [UseCases.retrieveNotifications]: anyone,
  [UseCases.retrieveUnReadNotificationsNbr]: anyone,
  [UseCases.markNotificationsAsRead]: anyone,
  [UseCases.retrieveDocuments]: anyone,
  [UseCases.uploadDocument]: anyone,
  [UseCases.createFolder]: anyone,
  [UseCases.removeFolder]: anyone,
  [UseCases.removeDocument]: anyone,
  [UseCases.downloadDocumentContent]: anyone,
  [UseCases.downloadFolderContent]: anyone,
  [UseCases.attachDocumentsToElement]: projectManagers,
  [UseCases.removeAttachedDocument]: projectManagers,
  [UseCases.moveDocument]: anyone,
  [UseCases.moveFolder]: anyone,
  [UseCases.retrieveElementAttachments]: anyone,
  [UseCases.search]: anyone,
  [UseCases.retrieveProjectTimeSheet]: projectManagers,
  [UseCases.retrieveTimeSheet]: anyone,
  [UseCases.reloadTimeSheet]: anyone,
  [UseCases.reloadProjectTimeSheet]: anyone,
  [UseCases.saveTimeRecord]: anyone,
  [UseCases.retrieveWeekTimeRecords]: anyone,
  [UseCases.deleteTimeRecord]: anyone,
  [UseCases.validateTimeRecordsUpToDate]: anyone,
  [UseCases.retrievePersonalTimeSheet]: anyone,
  [UseCases.validatePersonalTimeRecordsUpToDate]: anyone,
  [UseCases.retrieveProjectBudgetSynthesis]: anyone,
  [UseCases.createCostProfile]: [Roles.Admin],
  [UseCases.retrieveCostProfiles]: [Roles.Admin],
  [UseCases.editCostProfile]: [Roles.Admin],
  [UseCases.removeCostProfile]: [Roles.Admin],
  [UseCases.assignCostProfileToResource]: [Roles.Admin],
  [UseCases.removeCostProfileFromResource]: [Roles.Admin],
  [UseCases.retrieveCostProfileCostsHistory]: [Roles.Admin],
  [UseCases.removeCost]: [Roles.Admin],
  [UseCases.editCost]: [Roles.Admin],
  [UseCases.editProjectAllocatedBudget]: [Roles.Admin],
  [UseCases.retrieveWorkLoads]: anyone,
  [UseCases.retrieveCapacity]: anyone,
  [UseCases.retrieveWorkload]: anyone,
  [UseCases.retrieveWorkloadProjects]: anyone,
  [UseCases.retrieveOccupancy]: anyone,
  [UseCases.adjustWorkload]: anyone,
  [UseCases.adjustCapacity]: anyone,
  [UseCases.constrainElements]: projectManagers,
  [UseCases.editConstraint]: projectManagers,
  [UseCases.removeConstraint]: projectManagers,
  [UseCases.retrieveElementConstraints]: anyone,
  [UseCases.retrieveConstraint]: anyone,
  [UseCases.createUnavailability]: anyone,
  [UseCases.retrieveUnavailabilityReasons]: anyone,
  [UseCases.retrieveUnavailabilities]: anyone,
  [UseCases.editUnavailability]: [Roles.Admin],
  [UseCases.removeUnavailability]: [Roles.Admin],
  [UseCases.exportData]: [Roles.Admin],
  [UseCases.requestExportBiRun]: [Roles.Admin],
  [UseCases.retrieveExportBiRun]: [Roles.Admin],
  [UseCases.retrieveStrategicAxes]: [Roles.Admin],
  [UseCases.createStrategicAxis]: [Roles.Admin],
  [UseCases.retrieveOrganizationalUnits]: [Roles.Admin],
  [UseCases.retrieveAllOrganizationalUnits]: [Roles.Admin],
  [UseCases.createOrganizationalUnit]: [Roles.Admin],
  [UseCases.editOrganizationalUnit]: [Roles.Admin],
  [UseCases.removeOrganizationalUnit]: [Roles.Admin],
  [UseCases.moveOrganizationalUnit]: [Roles.Admin],
  [UseCases.updateOrganizationalUnit]: [Roles.Admin],
  [UseCases.retrieveAccessRestrictions]: anyone,
  [UseCases.editAccessRestrictions]: [Roles.Admin],
  [UseCases.removeStrategicAxis]: [Roles.Admin],
  [UseCases.editStrategicAxis]: [Roles.Admin],
  [UseCases.createProjectStrategicAxis]: [Roles.Admin, Roles.ProjectManager],
  [UseCases.retrieveProjectStrategicAxes]: [Roles.Admin],
  [UseCases.editProjectStrategicAxis]: [Roles.Admin, Roles.ProjectManager],
  [UseCases.createCalendarExportLink]: anyone,
  [UseCases.retrieveCalendarExportLinks]: anyone,
  [UseCases.createVideoConferenceCall]: anyone,
  [UseCases.retrieveVideoConferenceCallLinks]: anyone,
  [UseCases.retrieveResourceProjects]: [Roles.Admin],
  [UseCases.retrieveProjectExpenses]: anyone,
  [UseCases.createProjectExpense]: anyone,
  [UseCases.retrieveProjectExpenseTypes]: anyone,
  [UseCases.createProjectExpenseType]: anyone,
  [UseCases.removeProjectExpense]: anyone,
  [UseCases.editProjectExpense]: anyone,
  [UseCases.assignManagerToResource]: [Roles.Admin],
  [UseCases.unAssignManagerFromResource]: [Roles.Admin],
  [UseCases.assignEveryoneToManager]: [Roles.Admin],
  [UseCases.removeSubordinates]: [Roles.Admin],
  [UseCases.retrieveResourceSubordinates]: [Roles.Admin],
  [UseCases.retrieveGantt]: anyone,
  [UseCases.retrieveZoneGantt]: anyone,
  [UseCases.retrieveProjectLogs]: [Roles.ProjectManager, Roles.Admin],
  [UseCases.retrieveSecondaryCriteriaTemplate]: [
    Roles.Admin,
    Roles.ProjectManager,
  ],
  [UseCases.editSecondaryCriteriaTemplate]: [Roles.Admin, Roles.ProjectManager],
  [UseCases.retrieveWbsConfiguration]: projectManagers,
  [UseCases.editWbsConfiguration]: projectManagers,
  [UseCases.retrieveProjectDetailsConfiguration]: projectManagers,
  [UseCases.editProjectDetailsConfiguration]: projectManagers,
  [UseCases.retrieveResourceDetailsConfiguration]: projectMembers,
  [UseCases.editResourceDetailsConfiguration]: projectManagers,
  [UseCases.retrieveProjectElementDetailsConfiguration]: projectMembers,
  [UseCases.editProjectElementDetailsConfiguration]: projectManagers,
  [UseCases.retrieveProjectListingConfiguration]: [Roles.Admin],
  [UseCases.editProjectListingConfiguration]: projectManagers,
  [UseCases.retrieveResourcesListingConfiguration]: [Roles.Admin],
  [UseCases.editResourcesListingConfiguration]: [Roles.Admin],
  [UseCases.retrieveTimeRecordsListingConfiguration]: [Roles.Admin],
  [UseCases.editTimeRecordsListingConfiguration]: [Roles.Admin],
  [UseCases.retrieveProjectMembersListingConfiguration]: projectManagers,
  [UseCases.editProjectMembersListingConfiguration]: projectManagers,
  [UseCases.createCalendarHolidays]: [Roles.Admin],
  [UseCases.retrieveCalendarsHolidays]: anyone,
  [UseCases.removeCalendarHolidays]: [Roles.Admin],
  [UseCases.editCalendarHolidays]: [Roles.Admin],
  [UseCases.removeHoliday]: [Roles.Admin],
  [UseCases.assignPersonalCalendarHolidays]: anyone,
  [UseCases.retrievePersonalCalendarHolidays]: anyone,
  [UseCases.assignDefaultCalendarHolidaysToPlanzone]: [Roles.Admin],
  [UseCases.retrieveDefaultCalendarHolidays]: [Roles.Admin],
  [UseCases.assignCalendarHolidaysToResource]: [Roles.Admin],
  [UseCases.retrieveResourceCalendarHolidays]: [Roles.Admin],
  [UseCases.retrievePlanzoneLogs]: anyone,
  [UseCases.disableProjectResource]: [Roles.Admin, Roles.ProjectManager],
  [UseCases.retrieveProjectResourceAllocations]: [
    Roles.Admin,
    Roles.ProjectManager,
  ],
  [UseCases.activateProjectResource]: [Roles.Admin, Roles.ProjectManager],
  [UseCases.disableResource]: [Roles.Admin],
  [UseCases.activateResource]: [Roles.Admin],
  [UseCases.retrieveDocumentTemplatesFolder]: anyone,
  [UseCases.createDocument]: anyone,
  [UseCases.retrieveDocumentKey]: anyone,
  [UseCases.retrieveDocumentEditionToken]: anyone,
  [UseCases.updateFolder]: projectManagers,
  [UseCases.retrievePrivateDocuments]: anyone,
  [UseCases.editProjectAllocatedResourcesBudget]: [Roles.Admin],
  [UseCases.editProjectAllocatedExpensesBudget]: [Roles.Admin],
  [UseCases.copyDocument]: anyone,
  [UseCases.updateDocument]: anyone,
  [UseCases.unAssignCalendarHolidaysFromResource]: [Roles.Admin],
  [UseCases.unAssignPersonalCalendarHolidays]: anyone,
  [UseCases.addPlanzoneResourceToProjects]: [Roles.Admin],
  [UseCases.changePlanzoneLogo]: [Roles.Admin],
  [UseCases.changeProfilePicture]: anyone,
  [UseCases.addIdentityProvider]: [Roles.Admin],
  [UseCases.retrieveIdentityProviders]: [Roles.Admin],
  [UseCases.removeIdentityProvider]: [Roles.Admin],
  [UseCases.editIdentityProvider]: [Roles.Admin],
  [UseCases.retrieveOptimizedGantt]: anyone,
  [UseCases.retrieveConstraints]: anyone,
  [UseCases.retrieveWbsMeta]: anyone,
  [UseCases.retrieveGanttCalendarRange]: anyone,
  [UseCases.duplicateProject]: [
    Roles.Admin,
    Roles.SubAdmin,
    Roles.ProjectManager,
  ],
  [UseCases.transferProjectResourceAllocations]: projectManagers,
  [UseCases.editTimesheetDeadline]: [Roles.Admin],
  [UseCases.editAllocationWarning]: [Roles.Admin],
  [UseCases.submitMessage]: [Roles.Admin],
  [UseCases.retrieveConversations]: [Roles.Admin],
  [UseCases.getConversationMessages]: [Roles.Admin],
  [UseCases.deleteConversation]: [Roles.Admin],
  [UseCases.cancelJob]: [Roles.Admin],
  [UseCases.resetMemory]: [Roles.Admin],
};

export { permissions };
